import React, { useState, useEffect, useContext } from 'react'
import ShowMoreText from 'react-show-more-text'
import { XL_SIZE } from '../../utils/constants'
import { useParams } from 'react-router'
import {
  GET_ACTIVE_SUBSCRIPTIONS,
  IPTV_CHANNEL_FILTER,
  IPTV_CHANNEL_CONTENT_URL,
} from '../../utils/apiUrls'
import useAxios from 'axios-hooks'
import { getImage, getVideoThumbnail } from '../../utils/helper'
import { AppContext } from '../../context/app'
import { intersectionWith, isEmpty, isEqual } from 'lodash'
import { SubscriptionContext } from '../../context/subscription'
// import moment from 'moment'
import { useInterval } from '../../hooks/useInterval'
import { useTranslation } from 'react-i18next'
import ChannelPlayer from '../player/channelPlayer'
import ReviewsListing from '../../components/reviewRating'
import CommentListing from '../../components/comments'
import ChannelBottom from '../channel/channelBottom'
import { withTheme } from 'theming'
import IptvPlaylistEPG from './iptvPlaylistEPG'
import CustomImage from '../../components/customImage/customImage'
import LockIcon from '../../assets/lock-icon.svg'

const IptvPlaylist = ({ theme }) => {
  const channelId = useParams().channelId
  const { t } = useTranslation()
  const { isLogin, setOpenLoginModal, setModalType, accountObj } = useContext(AppContext)
  const {
    activeSubscription,
    setOpenSubscriptionModal,
    setSubscriptions,
    setRedirectionUrl,
    setContentPartners,
  } = useContext(SubscriptionContext)

  const [detailObj, setDetailObj] = useState({})
  const [showMore, setShowMore] = useState(false)
  const [isChannelSubscribed, setIsChannelSubscribed] = useState(false)
  const [currentPlayingContent, setCurrentPlayingContent] = useState({})
  const [videoSource, setVideoSource] = useState(null)
  const [videoURLError, setVideoURLError] = useState('')
  const [iptvLoader, setIptvLoader] = useState(false)
  const [isXL, setIsXL] = useState(window.innerWidth >= XL_SIZE)

  let isRatingOnly = accountObj?.od_resource_allowances?.rating_review === 'RATING'

  const [{ data: channelDetails, loading: channelDetailsLoading }, channelDetailsCall] = useAxios(
    {
      url: IPTV_CHANNEL_FILTER,
      method: 'post',
    },
    {
      manual: true,
    },
  )

  const [{ loading: activeSubLoading }, activeSubCall] = useAxios(
    {
      url: GET_ACTIVE_SUBSCRIPTIONS,
      method: 'get',
    },
    { manual: true },
  )

  const [, getCurrentPlayingUrl] = useAxios(
    {
      url: `${IPTV_CHANNEL_CONTENT_URL}/${channelId}`,
      method: 'get',
    },
    { manual: true },
  )

  useEffect(() => {
    setIptvLoader(channelDetailsLoading || activeSubLoading)
  }, [channelDetailsLoading, activeSubLoading])

  useEffect(() => {
    const handleResize = () => {
      setIsXL(window.innerWidth >= XL_SIZE)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setIsChannelSubscribed(true)
    setShowMore(false)
    setVideoSource(null)
    setVideoURLError('')
    setCurrentPlayingContent({})

    channelDetailsCall({
      data: {
        filters: { channel_ids: [channelId] },
        page: '1',
      },
    })
  }, [channelId])

  const checkPlanExist = (contentActiveSubscription, userSubscription) => {
    return !isEmpty(intersectionWith(contentActiveSubscription, userSubscription, isEqual))
  }

  const emptySubcheck = async (channelSub) => {
    const subRes = await activeSubCall()
    const activeSub = subRes.data.data
      .filter((item) => item.status)
      .map((item) => item.subscription)

    return !isEmpty(intersectionWith(channelSub, activeSub, isEqual))
  }

  const checkRestriction = async (channelDetail, openModal = false) => {
    if (!channelDetail?.is_restricted) {
      setIsChannelSubscribed(true)
      return true
    }

    if (!isLogin) {
      if (openModal) {
        setModalType('login')
        setOpenLoginModal(true)
      }
      setIsChannelSubscribed(false)
      return false
    }

    const channelSub = [
      ...(channelDetail?.active_subscription || []).map((item) => item._id),
      ...(channelDetail?.active_ppv || []).map((item) => item._id),
    ]

    const planExist = isEmpty(activeSubscription)
      ? await emptySubcheck(channelSub)
      : checkPlanExist(channelSub, activeSubscription)

    if (!planExist) {
      setIsChannelSubscribed(false)
      if (openModal) {
        setRedirectionUrl(window.location.pathname)
        setContentPartners(channelDetail?.owner ? channelDetail.owner : [])
        setSubscriptions(channelSub)
        setOpenSubscriptionModal(true)
      }
      return false
    }

    setIsChannelSubscribed(true)
    return true
  }

  const fetchCurrentPlayingContent = async () => {
    if (!isChannelSubscribed) return

    const requestSentAtMs = Date.now()

    try {
      const response = await getCurrentPlayingUrl()
      const result = response?.data

      if (!result?.success || isEmpty(result?.data)) return

      const { current_playing_url: playUrl, current_program: program } = result.data

      if (isEmpty(program)) {
        setCurrentPlayingContent({})
        setVideoSource(null)
        setVideoURLError('')
        return
      }

      // `offset_seconds` reflects the program's position as of the server's response, not
      // the moment we actually apply it here. Compensate for the round-trip network delay so
      // the resume position stays aligned with the true live position instead of lagging behind.
      const networkDelaySeconds = Math.max(0, (Date.now() - requestSentAtMs) / 1000)

      const nextContent = {
        ...program,
        title: program?.title || program?.name || program?.file_name || 'Untitled Program',
        playUrl,
        offset_seconds: Number(program?.offset_seconds || 0) + networkDelaySeconds,
      }

      // Always refresh program metadata (safe: does not remount the player).
      setCurrentPlayingContent(nextContent)

      if (!playUrl) {
        setVideoSource(null)
        setVideoURLError('Unable to play this program. Stream URL is missing.')
        return
      }

      // Only swap the active source when the playing asset actually changes,
      // so an unchanged program keeps playing smoothly across polls.
      if (playUrl === videoSource) return

      setVideoURLError('')
      setVideoSource(playUrl)
    } catch (error) {
      console.error('Current playing content fetch failed', error)
    }
  }

  useEffect(() => {
    const syncChannelDetails = async () => {
      if (!channelDetails || !channelDetails.success || isEmpty(channelDetails?.data)) return

      const channelDetail = channelDetails.data[0]
      setDetailObj(channelDetail)

      const isAllowed = await checkRestriction(channelDetail)
      if (!isAllowed) {
        setCurrentPlayingContent({})
        setVideoSource(null)
        return
      }

      await fetchCurrentPlayingContent()
    }

    syncChannelDetails()
  }, [channelDetails, isLogin, activeSubscription])

  useInterval(() => {
    fetchCurrentPlayingContent()
  }, 10000)

  const getResumeTime = () => {
    if (isEmpty(currentPlayingContent)) return 0

    const offsetSeconds = Number(currentPlayingContent?.offset_seconds)
    if (!(offsetSeconds > 0)) return 0

    const maxDuration =
      Number(currentPlayingContent?.duration) > 0
        ? Math.floor(Number(currentPlayingContent.duration))
        : null

    const resumeSeconds = Math.floor(offsetSeconds)
    if (!maxDuration) return resumeSeconds
    return Math.min(resumeSeconds, Math.max(0, maxDuration - 1))
  }

  const menuComponents = [
    {
      title: isRatingOnly ? t('rating') : t('reviews'),
      child: (
        <ReviewsListing
          type={'channel'}
          id={channelId}
          title={detailObj?.title}
          isRefreshed={channelDetailsLoading}
        />
      ),
    },
    {
      title: t('comments'),
      child: <CommentListing type={'channel'} id={channelId} theme={theme} />,
    },
  ]

  return (
    <div className="w-full h-full">
      <div className="flex flex-col xl:flex-row xl:justify-between xl:mt-3 px-0 2xl:max-w-[1630px] xl:max-w-[1297px] 2xl:h-[573px] xl:h-[433px] mx-auto">
        <div className="flex xl:hidden w-full justify-center items-center">
          <CustomImage
            imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.logo?.website?.s3_key)}
            alt="logo"
            imgClass="h-9 w-[101px]"
            loaderClass="h-9 w-[101px]"
          />
        </div>

        <div className="mb-3 xl:hidden text-center">
          <h3 className="test-xs font-semibold mb-1 text-white">{detailObj?.name}</h3>
        </div>

        <div
          className={`${
            iptvLoader && 'animate-pulse'
          } bg-black rounded-xl w-full xl:w-[769px] 2xl:w-[1018px] mb-3 xl:mb-0 overflow-hidden`}
          style={{ aspectRatio: '1.777' }}
        >
          {videoSource && (
            <ChannelPlayer
              contentInfo={currentPlayingContent}
              contentWatchUrl={videoSource}
              resumeTime={getResumeTime()}
              watermarkLogo={detailObj?.logo?.website?.s3_key}
              detailObj={detailObj}
            />
          )}

          {videoURLError && !videoSource ? (
            <div className="w-full h-full flex justify-center items-center">
              <p
                style={{ color: '#cecece' }}
                className="text-xs md:text-base xl:text-lg text-center font-semibold"
              >
                {videoURLError}
              </p>
            </div>
          ) : null}

          {!videoSource ? (
            !isChannelSubscribed ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="flex px-5 md:px-0">
                  <img
                    src={LockIcon}
                    style={{ width: '18.5px', height: '24.6px' }}
                    alt="LockIcon"
                  />
                  <p
                    style={{ color: '#cecece' }}
                    className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                  >
                    {t('this_channel_not_included')}
                  </p>
                </div>
                <button
                  style={{ background: '#09e9e9', color: '#000' }}
                  className="w-[70%] text-xs md:text-base xl:text-lg rounded-md xl:rounded-lg mt-4 xl:mt-6 py-1"
                  onClick={() => checkRestriction(detailObj, true)}
                >
                  {t('upgrade_membership_to_watch')}
                </button>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <CustomImage
                  imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.logo?.website?.s3_key)}
                  imgAlt="channelimg"
                  imgClass="h-6 w-9 md:h-12 md:w-20"
                  loaderClass="h-6 w-9 md:h-12 md:w-20"
                />
                <p
                  style={{ color: '#cecece' }}
                  className="md:ml-2 text-xs md:text-base xl:text-lg text-center"
                >
                  {t('no_programs_shown_at_moment')}
                </p>
              </div>
            )
          ) : null}
        </div>

        <div
          className={`${
            iptvLoader && 'animate-pulse'
          } w-full xl:w-[493px] 2xl:w-[561px] flex flex-col px-3 xl:px-0 relative`}
        >
          <div className="hidden w-full xl:flex justify-center items-center">
            <CustomImage
              imageUrl={getImage(accountObj?.cf_domain_name, detailObj?.logo?.website?.s3_key)}
              alt="logo"
              imgClass="xl:h-[50px] 2xl:h-[71px]"
              loaderClass="xl:h-[50px] 2xl:h-[71px]"
              imgStyle={{ aspectRatio: '1.7857' }}
              loaderStyle={{ aspectRatio: '1.7857' }}
            />
          </div>

          <div className="hidden xl:block text-center mt-1">
            <h3 className="2xl:text-[22px] text-base font-semibold mb-1 text-white">
              {detailObj?.name}
            </h3>
            <p className="2xl:text-xs text-[11px] mt-3 px-4" style={{ color: '#ACACAC' }}>
              {detailObj?.description}
            </p>
          </div>

          <div
            style={{ borderTop: '1px solid #707070' }}
            className="hidden xl:block xl:mt-[14px]"
          />

          {!isEmpty(currentPlayingContent) ? (
            <div className="text-sm uppercase mt-2 xl:mt-3 2xl:mt-4" style={{ color: '#007FFF' }}>
              {t('streaming_now')}
            </div>
          ) : null}

          {!isEmpty(currentPlayingContent) ? (
            <div className="flex gap-4 xl:gap-5 mb-3 xl:mb-4 flex-1 overflow-hidden">
              <div style={{ width: 'calc(100% - 160px)' }} className="flex-1 flex flex-col">
                <p
                  style={{ overflowWrap: 'break-word' }}
                  className="font-bold mb-1 text-sm xl:text-sm 2xl:text-base text-white"
                >
                  {currentPlayingContent?.title}
                </p>
                <div className="mb-3 xl:mb-0 flex-1 overflow-y-auto hidden-scrollbar-class">
                  <p className="text-xs mb-3" style={{ color: '#ACACAC' }}>
                    <ShowMoreText
                      lines={isXL ? 4 : 3}
                      more={<span style={{ color: '#4DA6FF' }}>show more</span>}
                      less={<span style={{ color: '#4DA6FF' }}>show less</span>}
                      anchorClass="text-sm cursor-pointer"
                      className="w-full text-xs"
                      expanded={showMore}
                      onClick={() => setShowMore(!showMore)}
                      truncatedEndingComponent={'... '}
                    >
                      <p className="w-full">{currentPlayingContent?.description}</p>
                    </ShowMoreText>
                  </p>
                </div>
              </div>

              <div className="w-[143px] xl:w-[151px] 2xl:w-48">
                <CustomImage
                  imageUrl={getVideoThumbnail(
                    accountObj?.cf_domain_name,
                    currentPlayingContent?.asset_id,
                  )}
                  alt="ContentImage"
                  imgClass="w-full h-20 xl:h-[85px] 2xl:h-[107px] object-contain bg-black"
                  style={{ border: '0.5px solid #FFFFFF', borderRadius: '2px' }}
                />
              </div>
            </div>
          ) : null}

          <hr className="mb-3 xl:hidden border-gray-700" />

          <div
            style={{ background: theme.c1 }}
            className="xl:absolute bottom-0 flex items-center gap-2 xl:gap-5 text-[10px] xl:text-xs 2xl:text-sm text-white py-1"
          >
            <div className="flex items-center">
              <div
                className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
                style={{ background: '#11171F', border: '1px solid #727272' }}
              ></div>
              <span className="2xl:ml-3 xl:ml-[10px] ml-1">Program</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
                style={{ background: '#141446', border: '1px solid #1F51FF' }}
              ></div>
              <span className="2xl:ml-3 xl:ml-[10px] ml-1">Streaming Now</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
                style={{ background: '#5C1921', border: '1px solid #FF3131' }}
              ></div>
              <span className="2xl:ml-3 xl:ml-[10px] ml-1">LIVE NOW</span>
            </div>
            <div className="flex items-center">
              <div
                className="w-3 h-3 xl:w-[14px] xl:h-[14px] 2xl:w-5 2xl:h-5 rounded"
                style={{ background: '#5C3819', border: '1px solid #FF9300' }}
              ></div>
              <span className="2xl:ml-3 xl:ml-[10px] ml-1">Upcoming LIVE</span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full 2xl:max-w-[1630px] xl:max-w-[1297px] mx-auto mt-[21px]">
        <IptvPlaylistEPG currentPlayingContent={currentPlayingContent} />
      </div>

      <div className="w-full 2xl:max-w-[88.8%] xl:max-w-[1297px] mx-auto mt-8">
        <ChannelBottom options={menuComponents} />
      </div>
    </div>
  )
}

export default withTheme(IptvPlaylist)
